import type { RefObject } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { SearchableOption } from './SearchableSelect'

export interface OptionGroup<TValue> {
  label: string | null
  items: SearchableOption<TValue>[]
}

const HEADING = 'px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-widest text-gray-400'

/**
 * The listbox inside SearchableSelect's popover.
 *
 * R1-F9: a listbox may own only options and groups of options. Each section
 * ("Most used" and every named group) is a `role="group"` named after its
 * heading; the visible heading is aria-hidden so it is not read as a row, and
 * the divider is presentational. "No matches" sits outside the listbox.
 */
export function SearchableOptionList<TValue>({
  listRef,
  listId,
  ariaLabel,
  maxHeight,
  top,
  groups,
  value,
  activeIndex,
  optionId,
  onPick,
}: {
  listRef?: RefObject<HTMLDivElement>
  listId: string
  ariaLabel: string
  maxHeight?: number
  top: SearchableOption<TValue>[]
  groups: OptionGroup<TValue>[]
  value: TValue | null
  activeIndex: number
  optionId: (i: number) => string
  onPick: (o: SearchableOption<TValue>) => void
}) {
  let rowIndex = -1
  const nextRow = () => {
    rowIndex += 1
    return rowIndex
  }
  const empty = top.length === 0 && groups.every((g) => g.items.length === 0)

  const row = (o: SearchableOption<TValue>, key: string) => {
    const i = nextRow()
    return (
      <OptionRow
        key={key}
        id={optionId(i)}
        option={o}
        selected={o.value === value}
        active={i === activeIndex}
        onSelect={() => onPick(o)}
      />
    )
  }

  return (
    <>
      <div
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={ariaLabel}
        className="overflow-y-auto py-1"
        style={{ maxHeight }}
      >
        {top.length > 0 && (
          <div role="group" aria-label="Most used">
            <p aria-hidden="true" className={HEADING}>
              Most used
            </p>
            {top.map((o) => row(o, `top-${String(o.value)}`))}
            <div role="presentation" className="my-1 border-t border-gray-100" />
          </div>
        )}

        {groups.map((g, gi) => (
          <div
            key={`g-${gi}-${g.label ?? 'main'}`}
            role={g.label ? 'group' : 'presentation'}
            aria-label={g.label ?? undefined}
          >
            {g.label && top.length === 0 && gi > 0 && (
              <div role="presentation" className="my-1 border-t border-gray-100" />
            )}
            {g.label && (
              <p aria-hidden="true" className={HEADING}>
                {g.label}
              </p>
            )}
            {g.items.map((o) => row(o, String(o.value)))}
          </div>
        ))}
      </div>
      {empty && <p className="px-3 py-6 text-center text-xs text-gray-400">No matches</p>}
    </>
  )
}

function OptionRow<TValue>({
  id,
  option,
  selected,
  active,
  onSelect,
}: {
  id: string
  option: SearchableOption<TValue>
  selected: boolean
  active: boolean
  onSelect: () => void
}) {
  return (
    <button
      id={id}
      type="button"
      role="option"
      tabIndex={-1}
      aria-selected={selected}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors',
        selected ? 'bg-brand-orange/5 text-brand-orange' : 'text-brand-rail hover:bg-gray-50',
        active && 'bg-gray-100',
      )}
    >
      {option.prefix && <span className="shrink-0">{option.prefix}</span>}
      <span className="flex-1 truncate font-medium">{option.label}</span>
      {option.hint && <span className="text-gray-400 font-normal shrink-0">{option.hint}</span>}
      {selected && <Check size={11} className="text-brand-orange shrink-0" />}
    </button>
  )
}
